/**
 * Single per-provider disconnect dispatch, shared by every surface that offers a Disconnect
 * action (Settings → Integrations, the Invoices page header). Before this, each surface kept its
 * own hand-maintained if/else ladder over FactoringProvider, so adding a provider meant editing
 * both — and one of them (WinFactor) was reached only as the OTHER ladder's default `else`
 * branch, so an unrecognized provider there would have silently issued a WinFactor disconnect
 * instead of reporting "not supported".
 */

import {
    removeArtisproCredentials,
    removeGsquaredCredentials,
    removeHaulPayCredentials,
    removeOtrSolutionsCredentials,
    removeOutgoCredentials,
    removeRtsToken,
    removeStratopayCredentials,
    removeTafsCredentials,
    removeTriumphToken,
    removeWexCredentials,
    removeWinfactorCredentials,
    removeEcapitalSession,
} from './auth'
import { removeTriumphSftpCredentials } from './triumph-sftp'
import { FactoringProvider } from './types'

interface FactoringDisconnectResult {
    success: boolean
    error?: string
}

const PROVIDER_DISCONNECT_FN: Partial<Record<FactoringProvider, () => Promise<FactoringDisconnectResult>>> = {
    [FactoringProvider.RTS]: removeRtsToken,
    [FactoringProvider.TRIUMPH]: removeTriumphToken,
    [FactoringProvider.TRIUMPH_SFTP]: removeTriumphSftpCredentials,
    [FactoringProvider.WINFACTOR]: removeWinfactorCredentials,
    [FactoringProvider.WEX]: removeWexCredentials,
    [FactoringProvider.STRATOPAY]: removeStratopayCredentials,
    [FactoringProvider.GSQUARED]: removeGsquaredCredentials,
    [FactoringProvider.HAUL_PAY]: removeHaulPayCredentials,
    [FactoringProvider.ARTISPRO]: removeArtisproCredentials,
    [FactoringProvider.OUTGO]: removeOutgoCredentials,
    // 'accounting' — both cards these disconnects come from are the invoicing side; the broker-
    // rating connection these providers also hold is untouched.
    [FactoringProvider.OTR_SOLUTIONS]: () => removeOtrSolutionsCredentials('accounting'),
    [FactoringProvider.TAFS]: () => removeTafsCredentials('accounting'),
    [FactoringProvider.ECAPITAL]: () => removeEcapitalSession('accounting'),
}

export async function disconnectFactoringProvider(
    providerType: FactoringProvider | null | undefined,
    providerLabel?: string
): Promise<FactoringDisconnectResult> {
    const disconnect = providerType ? PROVIDER_DISCONNECT_FN[providerType] : undefined
    if (!disconnect) return { success: false, error: `Disconnecting ${providerLabel || 'this provider'} is not supported yet` }
    return disconnect()
}
